import { AlertTriangle, Database, Download, RotateCcw, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api.ts';

interface BackupFile {
    name: string;
    sizeBytes: number;
    createdAt: string;
}

interface RestoreReport {
    tables: number;
    rows: number;
    skipped: string[];
    safetyBackup: string;
    /** Les tables dont le contenu LOCAL a été gardé. */
    preserved: string[];
}

const ko = (n: number): string =>
    (n < 1024 * 1024 ? `${Math.round(n / 1024)} ko` : `${(n / 1024 / 1024).toFixed(1)} Mo`);

const when = (iso: string): string =>
    new Date(iso).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });

export function AdminBackups() {
    const [backups, setBackups] = useState<BackupFile[]>([]);
    const [max, setMax] = useState(5);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<BackupFile | null>(null);
    const [typed, setTyped] = useState('');
    /* La configuration de l'installation est gardée PAR DÉFAUT.
     *
     * L'usage courant de cet écran est de rapatrier les données d'une
     * production sur une qualité pour les regarder : écraser au passage le
     * client OAuth, le serveur SMTP et les clés de notification ferait parler
     * la qualité au monde de la production. Restaurer une machine à
     * l'identique, configuration comprise, existe aussi — c'est le cas rare, et
     * il se demande. */
    const [garderConfig, setGarderConfig] = useState(true);
    const fichier = useRef<HTMLInputElement>(null);

    const reload = useCallback(async () => {
        try {
            const r = await api<{ backups: BackupFile[]; max: number }>('/admin/backups');
            setBackups(r.backups); setMax(r.max);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        }
    }, []);

    useEffect(() => { void reload(); }, [reload]);

    const create = async (): Promise<void> => {
        setBusy(true); setError(null); setNotice(null);
        try {
            const r = await api<{ backup: BackupFile; pruned: string[] }>(
                '/admin/backups', { method: 'POST' },
            );
            setNotice(r.pruned.length > 0
                ? `Sauvegarde créée. La plus ancienne a été supprimée pour tenir la limite de ${max}.`
                : 'Sauvegarde créée.');
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Sauvegarde impossible');
        } finally { setBusy(false); }
    };

    /* Envoyer le fichier TEL QUEL.
     *
     * Le client HTTP de l'application sérialise ce qu'on lui donne en JSON ;
     * une sauvegarde est un binaire compressé, et l'encoder pour la décoder
     * aussitôt n'apporterait qu'un tiers de volume en plus. On passe donc
     * directement par `fetch`, avec le type qui dit ce que c'est. */
    const importer = async (f: File): Promise<void> => {
        setBusy(true); setError(null); setNotice(null);
        try {
            const res = await fetch('/api/admin/backups/import', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/gzip' },
                body: f,
            });
            const corps = await res.json().catch(() => null) as
                { backup?: BackupFile; error?: string } | null;
            if (!res.ok) throw new ApiError(res.status, corps?.error ?? 'Import impossible');
            setNotice(`Fichier importé (${corps?.backup?.name ?? ''}). `
                + 'Il est dans la liste : à vous de le restaurer quand vous voudrez.');
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Import impossible');
        } finally {
            setBusy(false);
            // Le champ est vidé : sans cela, reprendre le MÊME fichier après une
            // erreur ne déclencherait aucun évènement, et l'écran paraîtrait
            // sourd.
            if (fichier.current) fichier.current.value = '';
        }
    };

    const remove = async (b: BackupFile): Promise<void> => {
        setBusy(true); setError(null);
        try {
            await api<void>(`/admin/backups/${b.name}`, { method: 'DELETE' });
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        } finally { setBusy(false); }
    };

    const restore = async (b: BackupFile): Promise<void> => {
        setBusy(true); setError(null); setNotice(null);
        try {
            const r = await api<{ report: RestoreReport }>(
                `/admin/backups/${b.name}/restore`,
                { method: 'POST', body: { preserveConfig: garderConfig } },
            );
            setConfirm(null); setTyped('');
            setNotice(`Restauré : ${r.report.rows} lignes sur ${r.report.tables} tables. `
                + (r.report.preserved.length > 0
                    ? 'La configuration de cette installation a été gardée. '
                    : 'La configuration vient du fichier. ')
                + `Un filet a été posé avant l'opération (${r.report.safetyBackup}).`);
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Restauration impossible');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            <section className="rounded-box bg-base-200 p-4">
                <h2 className="flex items-center gap-2 font-semibold">
                    <Database size={18} className="text-primary" /> Sauvegardes
                </h2>
                {/* Dire ce que le fichier contient ET ce qu'il ne contient pas :
                    quelqu'un qui restaure doit savoir qu'il remplace aussi les
                    comptes, et que le schéma ne vient pas de là. */}
                <p className="mt-1 text-xs opacity-60">
                    Toutes les données de l'application : comptes, matériel, séances, coups,
                    parties, réglages. La structure de la base n'y est pas — elle est
                    reconstruite par l'application au démarrage. {max} sauvegardes sont
                    conservées ; au-delà, la plus ancienne part.
                </p>

                {/* Importer, à côté de créer : les deux font entrer une
                    sauvegarde dans la liste, l'une en la prenant ici, l'autre
                    en la recevant d'ailleurs. Rien n'est remplacé à ce
                    moment-là — le fichier rejoint la liste, et la restauration
                    reste un second geste, délibéré. */}
                {/* Le sélecteur natif est CACHÉ, et ouvert par un bouton à nous.
                    Son libellé vient de la langue du navigateur et ne se change
                    pas : il affichait « Choose File » au milieu d'une application
                    entièrement française. Le bouton porte le texte, l'input garde
                    le rôle. */}
                <input ref={fichier} type="file" accept=".gz,application/gzip"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void importer(f);
                    }} />
                <button type="button" disabled={busy}
                    onClick={() => fichier.current?.click()}
                    className="btn btn-outline btn-block mt-3 gap-2 tap-target">
                    <Upload size={18} /> Importer une sauvegarde
                </button>
                <p className="mt-1 text-xs opacity-60">
                    Un fichier .json.gz pris sur une autre installation. Il est vérifié
                    à l’arrivée, puis rejoint la liste ci-dessous.
                </p>

                <button type="button" disabled={busy} onClick={() => void create()}
                    className="btn btn-primary mt-3 gap-2 tap-target">
                    <Database size={18} /> Sauvegarder maintenant
                </button>
            </section>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}
            {notice && <div role="status" className="alert alert-success text-sm"><span>{notice}</span></div>}

            {backups.length === 0 ? (
                <p className="rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                    Aucune sauvegarde pour l'instant.
                </p>
            ) : (
                <div className="space-y-2">
                    {backups.map((b) => (
                        <div key={b.name} className="rounded-box bg-base-200 p-4">
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold">{when(b.createdAt)}</span>
                                    <span className="block truncate font-mono text-xs opacity-50">
                                        {b.name}
                                    </span>
                                </span>
                                <span className="shrink-0 text-xs tabular-nums opacity-60">
                                    {ko(b.sizeBytes)}
                                </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <a href={`/api/admin/backups/${b.name}/download`}
                                    className="btn btn-ghost btn-sm gap-2">
                                    <Download size={16} /> Télécharger
                                </a>
                                <button type="button" disabled={busy}
                                    onClick={() => { setConfirm(b); setTyped(''); }}
                                    className="btn btn-outline btn-warning btn-sm gap-2">
                                    <RotateCcw size={16} /> Restaurer
                                </button>
                                <button type="button" disabled={busy} onClick={() => void remove(b)}
                                    aria-label={`Supprimer ${b.name}`}
                                    className="btn btn-ghost btn-sm btn-square ml-auto text-error">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Confirmation par saisie, et non par simple clic.
                Une restauration remplace TOUT, y compris les comptes : c'est le
                geste le plus destructeur de l'application, et le seul qu'on
                puisse déclencher en croyant faire l'inverse. Recopier le mot
                oblige à lire. */}
            {confirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-md rounded-box bg-base-200 p-5">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-warning">
                            <AlertTriangle size={20} /> Restaurer cette sauvegarde ?
                        </h2>
                        <p className="mt-2 text-sm opacity-80">
                            Sauvegarde du {when(confirm.createdAt)}.
                        </p>
                        <p className="mt-2 text-xs opacity-70">
                            Toutes les données actuelles sont remplacées : comptes, matériel,
                            séances, parties. Les comptes créés depuis cette sauvegarde
                            disparaîtront, et vous serez peut-être déconnecté.
                        </p>
                        <p className="mt-2 text-xs opacity-70">
                            Une sauvegarde de l'état actuel est prise juste avant, pour
                            pouvoir revenir en arrière.
                        </p>

                        {/* Le choix AVANT la confirmation tapée : on décide de
                            ce qu'on fait, puis on confirme qu'on le fait. */}
                        <label className="mt-4 flex items-start gap-3 rounded-field bg-base-100 p-3">
                            <input type="checkbox" className="checkbox mt-0.5 shrink-0"
                                checked={garderConfig}
                                onChange={(e) => setGarderConfig(e.target.checked)} />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold">
                                    Garder la configuration de cette installation
                                </span>
                                <span className="block text-xs opacity-70">
                                    Connexion Google, serveur d’envoi d’e-mails, clés de
                                    notification, modes de connexion. Décochez pour prendre
                                    aussi celle du fichier — à ne faire que pour remettre une
                                    machine à l’identique.
                                </span>
                            </span>
                        </label>

                        <label className="mt-4 block">
                            <span className="mb-1 block text-xs">
                                Tapez <span className="font-mono font-bold">RESTAURER</span> pour confirmer
                            </span>
                            <input
                                type="text"
                                autoFocus
                                className="input input-bordered w-full font-mono"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                            />
                        </label>

                        <div className="mt-4 space-y-2">
                            <button type="button"
                                disabled={busy || typed.trim().toUpperCase() !== 'RESTAURER'}
                                onClick={() => void restore(confirm)}
                                className="btn btn-warning btn-block gap-2 tap-target">
                                <RotateCcw size={18} /> Restaurer
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => { setConfirm(null); setTyped(''); }}>
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
